import { useEffect, useState, useCallback } from "react";
import { Plus, Loader2, X, ChevronDown, Trash2, Link2, Copy, Check } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fcfa, formatDate, statusColor, ORDER_STATUS_LABELS, PAYMENT_LABELS, PAYMENT_METHOD_LABELS, SOURCE_LABELS } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const inputCls = "border border-brand-line rounded-xl px-4 py-2.5 w-full focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none bg-white text-brand-ink";
const FILE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto p-6`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl text-brand-ink">{title}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-brand-cream rounded-full"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Orders() {
  const { user } = useAuth();
  const canEdit = ["patron", "vendeur"].includes(user?.role);
  const isPatron = user?.role === "patron";
  const [orders, setOrders] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const params = statusFilter ? `?status=${statusFilter}` : "";
    api.get(`/admin/orders${params}`).then(({ data }) => setOrders(data));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  if (orders === null) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink">Ventes & commandes</h1>
          <p className="text-brand-muted mt-1">{orders.length} commande(s)</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button onClick={() => setLinking(true)} data-testid="payment-link-button" className="bg-white border border-brand text-brand hover:bg-brand-cream rounded-full px-4 py-2.5 font-semibold flex items-center gap-2 text-sm">
              <Link2 className="w-4 h-4" /> Lien de paiement (Live)
            </button>
            <button onClick={() => setCreating(true)} data-testid="new-sale-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-4 py-2.5 font-semibold flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Vente directe
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {["", "nouvelle", "en_preparation", "livree", "annulee"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s || "all"}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${statusFilter === s ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-sub hover:bg-brand-cream"}`}>
            {s === "" ? "Toutes" : ORDER_STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      <div className="bg-white border border-brand-line rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-cream text-brand-sub text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Commande</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Total</th>
                <th className="px-4 py-3 font-semibold">Source</th>
                <th className="px-4 py-3 font-semibold">Paiement</th>
                <th className="px-4 py-3 font-semibold">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line">
              {orders.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-brand-muted">Aucune commande.</td></tr>
              ) : orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} onClick={() => setDetail(o)} className="hover:bg-brand-cream/40 cursor-pointer">
                  <td className="px-4 py-3">
                    <p className="font-medium text-brand-ink">{o.order_number}</p>
                    <p className="text-xs text-brand-muted">{formatDate(o.created_at)}</p>
                  </td>
                  <td className="px-4 py-3 text-brand-sub">{o.customer_name}<br /><span className="text-xs text-brand-muted">{o.customer_phone}</span></td>
                  <td className="px-4 py-3 font-semibold text-brand-ink">{fcfa(o.total)}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">{SOURCE_LABELS[o.source]}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${statusColor(o.payment_status)}`}>{PAYMENT_LABELS[o.payment_status]}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${statusColor(o.order_status)}`}>{ORDER_STATUS_LABELS[o.order_status]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && <NewSaleModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {linking && <PaymentLinkModal onClose={() => setLinking(false)} onSaved={load} />}
      {detail && <OrderDetailModal order={detail} canEdit={canEdit} isPatron={isPatron} onClose={() => setDetail(null)} onUpdated={() => { load(); setDetail(null); }} />}
    </div>
  );
}

function NewSaleModal({ onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState([]);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_address: "", payment_method: "cash", source: "direct", cash_received: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/admin/products").then(({ data }) => setProducts(data.filter((p) => p.stock_qty > 0))); }, []);

  const addLine = (pid) => {
    if (!pid) return;
    const p = products.find((x) => x.id === pid);
    if (lines.find((l) => l.product_id === pid)) return;
    setLines([...lines, { product_id: pid, name: p.name, price: p.price, qty: 1 }]);
  };
  const total = lines.reduce((s, l) => s + l.price * l.qty, 0);

  const save = async () => {
    setError("");
    if (lines.length === 0) return setError("Ajoutez au moins un produit.");
    setLoading(true);
    try {
      const payload = {
        ...form,
        customer_name: form.customer_name.trim() || "Client au comptoir",
        items: lines.map((l) => ({ product_id: l.product_id, qty: Number(l.qty) })),
      };
      await api.post("/admin/orders", payload);
      toast.success(form.payment_method === "cash" && form.cash_received ? "Vente encaissée (payée + remise)" : "Vente enregistrée");
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Modal title="Nouvelle vente directe" onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="sale-name" className={inputCls} placeholder="Nom du client (optionnel)" value={form.customer_name} onChange={set("customer_name")} />
          <div className="flex">
            <span className="inline-flex items-center px-2.5 rounded-l-xl border border-r-0 border-brand-line bg-brand-cream text-brand-sub text-sm font-semibold">+228</span>
            <input data-testid="sale-phone" className={inputCls + " rounded-l-none"} placeholder="90 00 00 00"
              value={form.customer_phone.replace(/^\+228\s?/, "")}
              onChange={(e) => setForm({ ...form, customer_phone: "+228 " + e.target.value.replace(/^\+?228\s?/, "") })} />
          </div>
        </div>
        <input className={inputCls} placeholder="Adresse (optionnel)" value={form.customer_address} onChange={set("customer_address")} />
        <div className="grid grid-cols-2 gap-3">
          <select className={inputCls} value={form.source} onChange={set("source")} data-testid="sale-source">
            <option value="direct">Vente directe</option>
            <option value="tiktok">TikTok</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          <select className={inputCls} value={form.payment_method} onChange={set("payment_method")} data-testid="sale-payment">
            <option value="cash">Cash</option>
            <option value="tmoney">Tmoney</option>
            <option value="flooz">Flooz</option>
          </select>
        </div>

        {form.payment_method === "cash" ? (
          <label className="flex items-center gap-2.5 rounded-xl border border-brand-line bg-brand-cream/60 px-4 py-3 cursor-pointer">
            <input type="checkbox" data-testid="sale-cash-received" checked={form.cash_received} onChange={(e) => setForm({ ...form, cash_received: e.target.checked })} className="w-4 h-4 accent-brand" />
            <span className="text-sm text-brand-ink font-medium">Espèces reçues (marquer <b>payé</b> et <b>remis au client</b>)</span>
          </label>
        ) : (
          <p className="text-xs text-brand-muted bg-brand-cream/60 rounded-xl px-4 py-3">Paiement {form.payment_method === "tmoney" ? "Tmoney" : "Flooz"} : la vente reste « en attente » ; la validation du paiement est réservée au patron/associé (qui reçoit la confirmation).</p>
        )}

        <div className="relative">
          <select className={inputCls + " appearance-none"} onChange={(e) => { addLine(e.target.value); e.target.value = ""; }} data-testid="sale-add-product">
            <option value="">+ Ajouter un produit…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} — {fcfa(p.price)} ({p.stock_qty} en stock)</option>)}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-3 top-3 text-brand-muted pointer-events-none" />
        </div>

        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={l.product_id} className="flex items-center gap-2 bg-brand-cream rounded-xl p-2">
              <span className="flex-1 text-sm text-brand-ink">{l.name}</span>
              <input type="number" min={1} value={l.qty} onChange={(e) => setLines(lines.map((x, xi) => xi === i ? { ...x, qty: e.target.value } : x))} className="w-16 border border-brand-line rounded-lg px-2 py-1 text-sm" />
              <span className="w-24 text-right text-sm font-medium">{fcfa(l.price * l.qty)}</span>
              <button onClick={() => setLines(lines.filter((_, xi) => xi !== i))} className="p-1.5 text-brand-secondary hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center pt-2 border-t border-brand-line">
          <span className="text-brand-sub">Total</span>
          <span className="font-heading font-black text-xl text-brand-ink" data-testid="sale-total">{fcfa(total)}</span>
        </div>
        {error && <p className="text-sm text-brand-secondary">{error}</p>}
        <button onClick={save} disabled={loading} data-testid="save-sale-button" className="w-full bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enregistrer la vente"}
        </button>
      </div>
    </Modal>
  );
}

function PaymentLinkModal({ onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [link, setLink] = useState("");
  const [orderNum, setOrderNum] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/admin/products").then(({ data }) => setProducts(data.filter((p) => p.stock_qty > 0)));
  }, []);

  const addLine = (pid) => {
    if (!pid) return;
    const p = products.find((x) => x.id === pid);
    if (lines.find((l) => l.product_id === pid)) return;
    setLines([...lines, { product_id: pid, name: p.name, price: p.price, qty: 1 }]);
  };
  const total = lines.reduce((s, l) => s + l.price * l.qty, 0);

  const generate = async () => {
    setError("");
    if (lines.length === 0) return setError("Ajoutez au moins un produit.");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/payment-link", {
        items: lines.map((l) => ({ product_id: l.product_id, qty: Number(l.qty) })),
        origin_url: window.location.origin,
        customer_name: name,
        customer_phone: phone,
        customer_address: address,
        source: "tiktok",
      });
      setLink(data.payment_url);
      setOrderNum(data.order_number);
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const waMsg = encodeURIComponent(`Bonjour ! Voici votre lien de paiement Noumabou (commande ${orderNum}) : ${link}`);

  return (
    <Modal title="Lien de paiement — Vente en live" onClose={onClose} wide>
      <div className="space-y-3">
        {!link ? (
          <>
            <p className="text-sm text-brand-muted">Choisissez les produits, générez le lien, puis envoyez-le au client sur WhatsApp / DM TikTok. Le client paie (Flooz / T-Money) et la commande passe automatiquement en « Payée ».</p>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="link-name" className={inputCls} placeholder="Nom du client" value={name} onChange={(e) => setName(e.target.value)} />
              <div className="flex">
                <span className="inline-flex items-center px-2.5 rounded-l-xl border border-r-0 border-brand-line bg-brand-cream text-brand-sub text-sm font-semibold">+228</span>
                <input data-testid="link-phone" className={inputCls + " rounded-l-none"} placeholder="90 00 00 00"
                  value={phone.replace(/^\+228\s?/, "")}
                  onChange={(e) => setPhone("+228 " + e.target.value.replace(/^\+?228\s?/, ""))} />
              </div>
            </div>
            <input data-testid="link-address" className={inputCls} placeholder="Adresse / coordonnées de livraison" value={address} onChange={(e) => setAddress(e.target.value)} />
            <div className="relative">
              <select className={inputCls + " appearance-none"} onChange={(e) => { addLine(e.target.value); e.target.value = ""; }} data-testid="link-add-product">
                <option value="">+ Ajouter un produit…</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} — {fcfa(p.price)} ({p.stock_qty} en stock)</option>)}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-3 text-brand-muted pointer-events-none" />
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={l.product_id} className="flex items-center gap-2 bg-brand-cream rounded-xl p-2">
                  <span className="flex-1 text-sm text-brand-ink">{l.name}</span>
                  <input type="number" min={1} value={l.qty} onChange={(e) => setLines(lines.map((x, xi) => xi === i ? { ...x, qty: e.target.value } : x))} className="w-16 border border-brand-line rounded-lg px-2 py-1 text-sm" />
                  <span className="w-24 text-right text-sm font-medium">{fcfa(l.price * l.qty)}</span>
                  <button onClick={() => setLines(lines.filter((_, xi) => xi !== i))} className="p-1.5 text-brand-secondary hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-brand-line">
              <span className="text-brand-sub">Total à payer</span>
              <span className="font-heading font-black text-xl text-brand-ink" data-testid="link-total">{fcfa(total)}</span>
            </div>
            {error && <p className="text-sm text-brand-secondary">{error}</p>}
            <button onClick={generate} disabled={loading} data-testid="generate-link-button" className="w-full bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Link2 className="w-4 h-4" /> Générer le lien</>}
            </button>
          </>
        ) : (
          <div className="space-y-3" data-testid="link-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <p className="text-sm text-emerald-800 font-medium">Lien généré ✓ — Commande {orderNum}</p>
              <p className="text-xs text-emerald-700 mt-1">Envoyez ce lien au client. Dès qu'il paie, la commande passe en « Payée ».</p>
            </div>
            <div className="flex items-center gap-2 bg-brand-cream rounded-xl p-3 break-all">
              <span className="flex-1 text-xs text-brand-ink">{link}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={copy} data-testid="copy-link-button" className="bg-brand-ink text-white rounded-full px-4 py-2.5 font-semibold flex items-center justify-center gap-2 text-sm">
                {copied ? <><Check className="w-4 h-4" /> Copié</> : <><Copy className="w-4 h-4" /> Copier</>}
              </button>
              <a href={`https://wa.me/${phone.replace(/\D/g, "")}?text=${waMsg}`} target="_blank" rel="noreferrer" data-testid="whatsapp-share-button" className="bg-[#25D366] text-white rounded-full px-4 py-2.5 font-semibold flex items-center justify-center gap-2 text-sm">
                Envoyer sur WhatsApp
              </a>
            </div>
            <button onClick={() => { setLink(""); setLines([]); setName(""); setPhone(""); setAddress(""); setZone(""); setOrderNum(""); }} className="w-full border border-brand-line rounded-full px-4 py-2.5 text-sm text-brand-sub hover:bg-brand-cream">Créer un autre lien</button>
          </div>
        )}
      </div>
    </Modal>
  );
}


function OrderDetailModal({ order, canEdit, isPatron, onClose, onUpdated }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const isMobileMoney = ["tmoney", "flooz"].includes(order.payment_method);
  const canValidatePayment = !(isMobileMoney && user?.role === "vendeur");

  const doAction = async (fn) => {
    setBusy(true);
    try { await fn(); toast.success("Mis à jour"); onUpdated(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };

  return (
    <Modal title={order.order_number} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${statusColor(order.order_status)}`}>{ORDER_STATUS_LABELS[order.order_status]}</span>
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${statusColor(order.payment_status)}`}>Paiement: {PAYMENT_LABELS[order.payment_status]}</span>
          <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{SOURCE_LABELS[order.source]}</span>
        </div>

        <div className="bg-brand-cream rounded-xl p-4 text-sm">
          <p className="font-semibold text-brand-ink">{order.customer_name}</p>
          <p className="text-brand-sub">{order.customer_phone}</p>
          {order.customer_address && <p className="text-brand-muted">{order.customer_address}</p>}
          <p className="text-xs text-brand-muted mt-1">Paiement: {PAYMENT_METHOD_LABELS[order.payment_method]} · {formatDate(order.created_at)}</p>
        </div>

        {(order.payment_ref || order.payment_proof_path) && (
          <div className="bg-brand-cream rounded-xl p-4 text-sm space-y-2">
            <p className="font-semibold text-brand-ink">Preuve de paiement</p>
            {order.payment_ref && <p className="text-brand-sub">Référence : <span className="font-medium text-brand-ink">{order.payment_ref}</span></p>}
            {order.payment_proof_path && (
              <a href={FILE_BASE + order.payment_proof_path} target="_blank" rel="noreferrer" data-testid="view-proof">
                <img src={FILE_BASE + order.payment_proof_path} alt="Preuve de paiement" className="max-h-48 rounded-lg border border-brand-line" />
              </a>
            )}
          </div>
        )}

        <div className="space-y-2">
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-brand-sub">{it.qty} × {it.name}</span>
              <span className="font-medium text-brand-ink">{fcfa(it.line_total)}</span>
            </div>
          ))}
          <div className="flex justify-between pt-2 border-t border-brand-line">
            {order.subtotal != null ? (
              <div className="w-full space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-brand-sub">Sous-total</span><span className="text-brand-ink">{fcfa(order.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-brand-sub">Livraison{order.delivery_label ? ` — ${order.delivery_label}` : ""}</span><span className="text-brand-ink">{order.delivery_fee > 0 ? fcfa(order.delivery_fee) : "À convenir"}</span></div>
                <div className="flex justify-between pt-1 border-t border-brand-line"><span className="font-semibold text-brand-ink">Total</span><span className="font-heading font-black text-brand-ink">{fcfa(order.total)}</span></div>
              </div>
            ) : (
              <>
                <span className="font-semibold text-brand-ink">Total</span>
                <span className="font-heading font-black text-brand-ink">{fcfa(order.total)}</span>
              </>
            )}
          </div>
          {isPatron && order.profit != null && (
            <div className="flex justify-between text-sm text-emerald-700"><span>Bénéfice</span><span className="font-semibold">{fcfa(order.profit)}</span></div>
          )}
        </div>

        {canEdit && order.order_status !== "annulee" && (
          <div className="space-y-3 pt-3 border-t border-brand-line">
            <div>
              <p className="text-xs text-brand-muted mb-1.5">Statut commande</p>
              <div className="flex flex-wrap gap-2">
                {["nouvelle", "en_preparation", "livree", "annulee"].map((s) => (
                  <button key={s} disabled={busy} data-testid={`set-status-${s}`} onClick={() => doAction(() => api.patch(`/admin/orders/${order.id}/status`, { order_status: s }))}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium border ${order.order_status === s ? "bg-brand-ink text-white border-brand-ink" : "border-brand-line text-brand-sub hover:bg-brand-cream"}`}>
                    {ORDER_STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-brand-muted mb-1.5">Paiement</p>
              {canValidatePayment ? (
                <div className="flex flex-wrap gap-2">
                  {["en_attente", "partiel", "paye"].map((s) => (
                    <button key={s} disabled={busy} data-testid={`set-payment-${s}`} onClick={() => doAction(() => api.patch(`/admin/orders/${order.id}/payment`, { payment_status: s, amount_paid: s === "partiel" ? order.total / 2 : undefined }))}
                      className={`text-xs px-3 py-1.5 rounded-full font-medium border ${order.payment_status === s ? "bg-brand-ink text-white border-brand-ink" : "border-brand-line text-brand-sub hover:bg-brand-cream"}`}>
                      {PAYMENT_LABELS[s]}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-brand-muted bg-brand-cream rounded-lg px-3 py-2" data-testid="payment-locked-note">Validation d'un paiement {order.payment_method === "tmoney" ? "Tmoney" : "Flooz"} réservée au patron / associé.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
